/**
 * @fileoverview Job active-range helpers for the Schedule page: compare JobNumber
 * `date_start` / `date_end` to the visible timeline window and to single-day clicks.
 * Schedule blocks are loaded for a bounded date window on the server; jobs are still
 * fetched in bulk and filtered on the client to the visible timeline (open-ended job
 * dates use unbounded past/future keys).
 */

import { toLocalCalendarDay, toLocalDateKey } from "../../../../utils/datetime/core";

/**
 * JobNumber date_start / date_end (inclusive) vs schedule visible window.
 * Open-ended jobs: missing start → unbounded past; missing end → unbounded future.
 * Both missing → job never appears on Schedule (no definable active span).
 */

const MIN_DATE_KEY = "0000-01-01";
const MAX_DATE_KEY = "9999-12-31";

/** @param {string|null|undefined} v */
function toDateKeyPart(v) {
  if (v == null || v === "") {
    return null;
  }
  return String(v).split("T")[0];
}

/**
 * Inclusive active span as string keys (YYYY-MM-DD) for comparisons.
 * @param {{ date_start?: string|null, date_end?: string|null }} job
 * @returns {{ startKey: string, endKey: string }|null}
 */
export function getJobInclusiveActiveKeys(job) {
  if (!job) {
    return null;
  }
  const startRaw = toDateKeyPart(job.date_start);
  const endRaw = toDateKeyPart(job.date_end);
  if (!startRaw && !endRaw) {
    return null;
  }
  return {
    startKey: startRaw ?? MIN_DATE_KEY,
    endKey: endRaw ?? MAX_DATE_KEY,
  };
}

/**
 * Whether the job's inclusive active range overlaps [visibleStartKey, visibleEndKey] inclusive.
 * @param {{ date_start?: string|null, date_end?: string|null }} job
 * @param {string} visibleStartKey
 * @param {string} visibleEndKey
 */
export function jobOverlapsVisibleRange(job, visibleStartKey, visibleEndKey) {
  const span = getJobInclusiveActiveKeys(job);
  if (!span) {
    return false;
  }
  return span.startKey <= visibleEndKey && visibleStartKey <= span.endKey;
}

/**
 * Whether a calendar day (YYYY-MM-DD) falls inside the job's inclusive active range.
 * @param {string} dayKey
 * @param {{ date_start?: string|null, date_end?: string|null }} job
 */
export function isDayWithinJobActiveRange(dayKey, job) {
  const span = getJobInclusiveActiveKeys(job);
  if (!span) {
    return false;
  }
  return dayKey >= span.startKey && dayKey <= span.endKey;
}

/**
 * Clamp a local calendar day key to [job.startKey, job.endKey] inclusive.
 * @param {string} dayKey
 * @param {{ date_start?: string|null, date_end?: string|null }} job
 * @returns {string}
 */
export function clampDayKeyToJobRange(dayKey, job) {
  const span = getJobInclusiveActiveKeys(job);
  if (!span) {
    return dayKey;
  }
  if (dayKey < span.startKey) {
    return span.startKey;
  }
  if (dayKey > span.endKey) {
    return span.endKey;
  }
  return dayKey;
}

/**
 * Clamp a calendar day to the job's inclusive range; returns {@link toLocalCalendarDay}-style noon local Date.
 * @param {Date|string|number} dateValue
 * @param {{ date_start?: string|null, date_end?: string|null }} job
 * @returns {Date}
 */
export function clampLocalDateToJobRange(dateValue, job) {
  const span = getJobInclusiveActiveKeys(job);
  if (!span) {
    return toLocalCalendarDay(dateValue);
  }
  let key = toLocalDateKey(dateValue);
  if (key < span.startKey) {
    key = span.startKey;
  }
  if (key > span.endKey) {
    key = span.endKey;
  }
  const [y, m, d] = key.split("-").map((n) => parseInt(n, 10));
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}
